import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { v, num, ClauseBlock, FieldRow, KeepTogether, Section, SectionNumberingProvider, SigningBlock, TemplateDocument, TemplateFooter, TemplateLetterhead, attachmentLabel, } from '../../../atoms/index.js';
import { expandPartyProjection, PartyBlock, COTENANT_SUFFIX, } from '../../../parties/index.js';
import { parseDateValue, parseNumberValue, postSigningRule, subtractMonths, } from '../../../notification-rules.js';
/**
 * Notification rules (R5). Bostadshyra has two lifecycle events worth
 * reminding on, BOTH gated on `lease_type === 'Bestämd tid'`:
 *   - termination_notice — lease_end_date − notice_months
 *   - lease_end          — lease_end_date itself
 *
 * Indefinite (`Tillsvidare`) contracts have no static deadline: both
 * resolvers return `null`, so the materializer emits zero rows. Users
 * can add custom reminders post-signing if needed. Praxis-driven rules
 * for indefinite leases are out of MVP scope (handoff with Haymanot).
 */
const bostadshyraNotificationRules = [
    postSigningRule({
        ruleKey: 'termination_notice',
        kind: 'termination_notice',
        label: 'Uppsägning',
        resolveTriggerAt: (values) => {
            if (values.lease_type !== 'Bestämd tid')
                return null;
            const leaseEnd = parseDateValue(values.lease_end_date);
            // The standard branch keeps `notice_months` hidden/unset, so read
            // the implied 3 months from `notice_type` — otherwise the default
            // contract would silently produce no uppsägning reminder.
            const notice = values.notice_type === '3 månader (standard)'
                ? 3
                : parseNumberValue(values.notice_months);
            if (!leaseEnd || notice === null || notice <= 0)
                return null;
            return subtractMonths(leaseEnd, notice);
        },
    }),
    // Lease-end reminders fire at custom 4mo + 2mo offsets, not the
    // default 3mo/1mo/1wk leads. The 4mo touch is the "decide whether to
    // renew or give notice" window; the 2mo touch is the "lease ending
    // soon" follow-up. Custom occurrences inline since they don't fit
    // the postSigningRule helper's standard-lead shape.
    {
        ruleKey: 'lease_end',
        kind: 'lease_end',
        label: 'Hyresavtalet löper ut',
        phase: 'post_signing',
        resolve: (ctx) => {
            if (ctx.values.lease_type !== 'Bestämd tid')
                return [];
            const leaseEnd = parseDateValue(ctx.values.lease_end_date);
            if (!leaseEnd)
                return [];
            return [
                {
                    triggerAt: leaseEnd,
                    fireAt: subtractMonths(leaseEnd, 4),
                    leadLabel: 'custom',
                    label: '4 månader före hyresavtalet löper ut',
                },
                {
                    triggerAt: leaseEnd,
                    fireAt: subtractMonths(leaseEnd, 2),
                    leadLabel: 'custom',
                    label: '2 månader före hyresavtalet löper ut',
                },
            ];
        },
    },
];
export const metadata = {
    templateKey: 'bostadshyra',
    version: 'v1',
    category: 'Bostadshyra',
    title: 'Bostadshyresavtal - Version 2026',
    shortTitle: 'Bostadshyresavtal',
    // Per legal: the officiellt lägenhetsnummer (the four-digit
    // population-register number) is the canonical identifier and
    // should drive the title. The landlord's own apartment number is
    // a fallback for contracts where the official one wasn't captured.
    // The object-name segment uses the gatuadress (e.g. "Sveavägen 42")
    // rather than the fastighetsbeteckning — laypeople identify their
    // apartment by street, not by cadastral designation.
    // Example: "Bostadshyresavtal – Sveavägen 42 Lgh 1201".
    titleSuffix: {
        key: 'property_official_apt_no',
        fallbackKey: 'property_landlord_apt_no',
        prefix: 'Lgh',
        propertyField: 'street',
    },
    formNumber: 'Formulär R-01',
    preamble: 'Undertecknande parter har denna dag träffat följande hyresavtal.',
    status: 'active',
    signingPlacementAnchors: 'per-party-label',
    partySlots: [
        {
            id: 'lessor',
            label: 'Hyresvärd',
            role: 'lessor',
            ordinal: 0,
            required: true,
        },
        {
            id: 'lessee_primary',
            label: 'Hyresgäst (primär)',
            signingLabel: 'Hyresgäst',
            role: 'lessee',
            ordinal: 0,
            required: true,
        },
        {
            id: 'lessee_co',
            label: 'Medhyresgäst',
            role: 'lessee',
            ordinal: 1,
            help: 'Valfritt — fyll i om kontraktet har en andra hyresgäst.',
        },
        {
            id: 'guarantor',
            label: 'Borgensman',
            role: 'guarantor',
            ordinal: 0,
            help: 'Valfritt — endast om någon går i borgen för hyran.',
            visibleWhen: { has_guarantor: true },
        },
    ],
    // Canonical party projection (ADR-0065). The lessor surfaces a full
    // contact set; the lessee omits address; the co-tenant mirrors the
    // lessee with the standard "(medhyresgäst)" suffix. The guarantor slot
    // is rendered out-of-band (not via the projection).
    partyProjection: [
        {
            slot: 'lessor',
            section: 'hyresvard',
            fields: [
                { attr: 'legal_name', key: 'landlord_name', requiredAtFinal: true },
                { attr: 'id_number', key: 'landlord_org_number' },
                { attr: 'address', key: 'landlord_address' },
                { attr: 'email', key: 'landlord_email' },
                { attr: 'phone', key: 'landlord_phone' },
            ],
        },
        {
            slot: 'lessee_primary',
            section: 'hyresgast',
            fields: [
                { attr: 'legal_name', key: 'tenant_name', requiredAtFinal: true },
                { attr: 'id_number', key: 'tenant_personnummer', help: 'PII — ej sökbart' },
                { attr: 'email', key: 'tenant_email' },
                { attr: 'phone', key: 'tenant_phone' },
            ],
        },
        {
            slot: 'lessee_co',
            section: 'hyresgast',
            labelSuffix: COTENANT_SUFFIX,
            fields: [
                { attr: 'legal_name', key: 'cotenant_name' },
                { attr: 'id_number', key: 'cotenant_personnummer', help: 'PII — ej sökbart' },
                { attr: 'email', key: 'cotenant_email' },
                { attr: 'phone', key: 'cotenant_phone' },
            ],
        },
    ],
    // Document sections in fixed order (per ADR-0049). § numbers are
    // computed from visibility at render time, so the conditional
    // Förhandlingsklausul no longer leaves a §9 → §11 gap when hidden.
    // `VariableField.section` values below are refs into this list.
    sections: [
        { ref: 'hyresvard', title: 'Hyresvärd' },
        { ref: 'hyresgast', title: 'Hyresgäst' },
        { ref: 'lagenhets-adress', title: 'Lägenhetens adress' },
        { ref: 'anvandning', title: 'Lägenhetens användning' },
        { ref: 'storlek', title: 'Lägenhetens storlek' },
        { ref: 'tillbehor', title: 'Till lägenheten hör' },
        { ref: 'hyrestid', title: 'Hyrestid' },
        { ref: 'uppsagning', title: 'Uppsägning' },
        { ref: 'hyressattningsmodell', title: 'Hyressättningsmodell' },
        {
            ref: 'forhandlingsklausul',
            title: 'Förhandlingsklausul',
            visibleWhen: { rent_model: 'Kollektiva hyresförhandlingar' },
        },
        { ref: 'hyra', title: 'Hyra' },
        { ref: 'tillagg', title: 'Tillägg' },
        { ref: 'betalning', title: 'Hyrans betalning' },
        { ref: 'deposition', title: 'Deposition' },
        { ref: 'garage', title: 'Garage / Bilplats' },
        { ref: 'reservnycklar', title: 'Reservnycklar' },
        { ref: 'sarskilda-bestammelser', title: 'Särskilda bestämmelser' },
        { ref: 'underskrift', title: 'Underskrifter' },
    ],
    // Editor field-panel grouping. Party sections (hyresvard/hyresgast) and
    // the property side of lagenhets-adress are realised by the dedicated
    // parties/property panels — the editor derives their § labels — so they
    // don't appear here. Sections not listed (Avtalsuppgifter, Borgensman)
    // render as their own standalone panels in schema order.
    inputSections: [
        {
            id: 'objektet',
            label: 'Hyresobjektet',
            sectionRefs: ['lagenhets-adress', 'anvandning', 'storlek', 'tillbehor'],
        },
        {
            id: 'hyrestid-uppsagning',
            label: 'Hyrestid & uppsägning',
            sectionRefs: ['hyrestid', 'uppsagning'],
        },
        {
            id: 'hyra-avgifter',
            label: 'Hyra & avgifter',
            sectionRefs: [
                'hyressattningsmodell',
                'forhandlingsklausul',
                'hyra',
                'tillagg',
                'betalning',
                'deposition',
            ],
        },
        {
            id: 'ovrigt',
            label: 'Övrigt',
            sectionRefs: ['garage', 'reservnycklar', 'sarskilda-bestammelser'],
        },
    ],
    notificationRules: bostadshyraNotificationRules,
};
export const variableSchema = [
    // Avtalsuppgifter — contract-level identifiers, top of the editor.
    {
        key: 'contract_number',
        label: 'Avtalsnummer',
        type: 'text',
        section: 'Avtalsuppgifter',
        requiredAtFinal: true,
        help: 'Egen referens på avtalet, t.ex. A-2042.',
        // Per-contract identifier — never carried over by copy-from (ADR-0050).
        scopes: { copy: 'never' },
    },
    {
        key: 'object_number',
        label: 'Objektsnummer',
        type: 'text',
        section: 'Avtalsuppgifter',
        help: 'Valfritt — egen referens på hyresobjektet.',
        scopes: { copy: 'never' },
    },
    // §1 Hyresvärd / §2 Hyresgäst + Medhyresgäst — generated from
    // `metadata.partyProjection` (ADR-0065). Keys are unchanged; labels are
    // resolved from the canonical vocabulary.
    ...expandPartyProjection(metadata),
    // §3 Lägenhetens adress — Fastighetsbeteckning + Postadress are property-linked.
    {
        key: 'property_municipality',
        label: 'Kommun',
        type: 'text',
        section: 'lagenhets-adress',
        requiredAtFinal: true,
        autofill: { kind: 'property', field: 'kommun' },
        scopes: { imported: true },
    },
    {
        key: 'property_designation',
        label: 'Fastighetsbeteckning',
        type: 'text',
        section: 'lagenhets-adress',
        requiredAtFinal: true,
        autofill: { kind: 'property', field: 'name' },
        scopes: { imported: { from: 'property.designation' } },
    },
    { key: 'property_staircase', label: 'Trappor', type: 'text', section: 'lagenhets-adress', scopes: { imported: { from: 'property.floor' } } },
    {
        key: 'property_official_apt_no',
        label: 'Officiellt lägenhetsnummer',
        type: 'text',
        section: 'lagenhets-adress',
        help: 'Enligt lägenhetsregister (Lantmäteriet). Fyrsiffrigt nummer, t.ex. 1201, 1508, 0903.',
        // Per-unit identifier — never carried over by copy-from (ADR-0050).
        // (`imported.from` is an extraction path, not `autofill`, so it would
        //  otherwise copy as a plain value.)
        scopes: { imported: { from: 'property.unit_number' }, copy: 'never' },
    },
    {
        key: 'property_address',
        label: 'Postadress',
        type: 'textarea',
        section: 'lagenhets-adress',
        requiredAtFinal: true,
        help: 'Gatuadress',
        autofill: { kind: 'property', field: 'address' },
        scopes: { imported: { from: 'property.composed_address', requiredForReview: true } },
    },
    {
        key: 'property_landlord_apt_no',
        label: 'Hyresvärdens egna lägenhetsnummer',
        type: 'text',
        section: 'lagenhets-adress',
        help: 'Valfritt. Hyresvärdens interna beteckning (t.ex. A1003, Lgh 14). Visas i avtalet endast om det fylls i.',
        // No `from` path — the doc-parser doesn't surface the landlord's
        // internal label; `property.unit_number` carries the officiella
        // lägenhetsnummer and maps to property_official_apt_no.
        // Per-unit identifier — never carried over by copy-from (ADR-0050).
        scopes: { imported: true, copy: 'never' },
    },
    // §4 Lägenhetens användning
    {
        key: 'apartment_usage',
        label: 'Lägenhetens användning',
        type: 'select',
        section: 'anvandning',
        requiredAtFinal: true,
        options: [
            { value: 'för användning som stadigvarande bostad', label: 'För användning som stadigvarande bostad', code: 'stadigvarande', summary: 'Permanent home — the standard residential use.' },
            { value: 'för användning som företagsbostad', label: 'För användning som företagsbostad', code: 'foretagsbostad', summary: 'Corporate housing let to a company.' },
            { value: 'för användning som evakueringsbostad', label: 'För användning som evakueringsbostad', code: 'evakueringsbostad', summary: 'Temporary evacuation housing during renovation.' },
            { value: 'för användning som komplementbostad', label: 'För användning som komplementbostad', code: 'komplementbostad', summary: 'Supplementary/secondary dwelling.' },
            { value: 'annat', label: 'Annat', code: 'annat', summary: 'Other use, specified in free text.' },
        ],
        default: 'för användning som stadigvarande bostad',
    },
    {
        key: 'apartment_usage_other',
        label: 'Annat ändamål (specificera)',
        type: 'text',
        section: 'anvandning',
        help: 'Beskriv ändamålet',
        visibleWhen: { apartment_usage: 'annat' },
    },
    // §5 Lägenhetens storlek
    {
        key: 'apartment_type',
        label: 'Lägenhetstyp',
        type: 'select',
        section: 'storlek',
        requiredAtFinal: true,
        options: [
            { value: '1 rum och kokskåp', label: '1 rum och kokskåp', code: '1r_kokskap', summary: '1 room with a kitchenette cupboard.' },
            { value: '1 rum och kokvrå', label: '1 rum och kokvrå', code: '1r_kokvra', summary: '1 room with a kitchen nook.' },
            { value: '1 rum och kök', label: '1 rum och kök', code: '1r', summary: '1 room and kitchen.' },
            { value: '1,5 rum och kök', label: '1,5 rum och kök', code: '1_5r', summary: '1.5 rooms and kitchen.' },
            { value: '2 rum och kokvrå', label: '2 rum och kokvrå', code: '2r_kokvra', summary: '2 rooms with a kitchen nook.' },
            { value: '2 rum och kök', label: '2 rum och kök', code: '2r', summary: '2 rooms and kitchen.' },
            { value: '2,5 rum och kök', label: '2,5 rum och kök', code: '2_5r', summary: '2.5 rooms and kitchen.' },
            { value: '3 rum och kök', label: '3 rum och kök', code: '3r', summary: '3 rooms and kitchen.' },
            { value: '3,5 rum och kök', label: '3,5 rum och kök', code: '3_5r', summary: '3.5 rooms and kitchen.' },
            { value: '4 rum och kök', label: '4 rum och kök', code: '4r', summary: '4 rooms and kitchen.' },
            { value: '4,5 rum och kök', label: '4,5 rum och kök', code: '4_5r', summary: '4.5 rooms and kitchen.' },
            { value: '5 rum och kök', label: '5 rum och kök', code: '5r', summary: '5 rooms and kitchen.' },
            { value: '6 rum och kök', label: '6 rum och kök', code: '6r', summary: '6 rooms and kitchen.' },
            { value: '7 rum och kök', label: '7 rum och kök', code: '7r', summary: '7 rooms and kitchen.' },
            { value: '8 rum och kök', label: '8 rum och kök', code: '8r', summary: '8 rooms and kitchen.' },
        ],
        scopes: { imported: { from: 'property.room_count' } },
    },
    {
        key: 'apartment_area',
        label: 'Area i m²',
        type: 'number',
        min: 0,
        section: 'storlek',
        requiredAtFinal: true,
        scopes: { imported: { from: 'property.unit_size_sqm' } },
    },
    // §6 Till lägenheten hör
    { key: 'has_attic', label: 'Vindsutrymme ingår', type: 'boolean', section: 'tillbehor' },
    {
        key: 'attic_number',
        label: 'Vindsutrymme nr',
        type: 'text',
        section: 'tillbehor',
        visibleWhen: { has_attic: true },
    },
    { key: 'has_basement', label: 'Källarutrymme ingår', type: 'boolean', section: 'tillbehor' },
    {
        key: 'basement_number',
        label: 'Källarutrymme nr',
        type: 'text',
        section: 'tillbehor',
        visibleWhen: { has_basement: true },
    },
    // §7 Hyrestid
    {
        key: 'lease_type',
        label: 'Hyresform',
        type: 'select',
        section: 'hyrestid',
        requiredAtFinal: true,
        options: [
            { value: 'Tillsvidare', label: 'Tillsvidare', code: 'tillsvidare', summary: 'Open-ended lease (until further notice).' },
            { value: 'Bestämd tid', label: 'Bestämd tid', code: 'bestamd_tid', summary: 'Fixed-term lease with an end date.' },
        ],
        default: 'Tillsvidare',
        scopes: { imported: { from: 'dates.lease_type_sv' } },
    },
    { key: 'lease_start_date', label: 'Startdatum', type: 'date', section: 'hyrestid', requiredAtFinal: true, scopes: { imported: { from: 'dates.start', requiredForReview: true }, copy: 'manual' } },
    {
        key: 'lease_end_date',
        label: 'Slutdatum (vid bestämd tid)',
        type: 'date',
        section: 'hyrestid',
        durationFrom: 'lease_start_date',
        visibleWhen: { lease_type: 'Bestämd tid' },
        scopes: { imported: { from: 'dates.end' }, copy: 'manual' },
    },
    // §8 Uppsägning
    {
        key: 'notice_type',
        label: 'Uppsägningstyp',
        type: 'select',
        section: 'uppsagning',
        requiredAtFinal: true,
        options: [
            { value: '3 månader (standard)', label: '3 månader (standard)', code: 'standard_3m', summary: 'Statutory default — three months notice.' },
            { value: 'Annat antal månader', label: 'Annat antal månader', code: 'annat', summary: 'A custom notice period, specified in months.' },
        ],
        default: '3 månader (standard)',
        scopes: { imported: true },
    },
    {
        key: 'notice_months',
        label: 'Antal månader',
        type: 'number',
        min: 0,
        integer: true,
        section: 'uppsagning',
        visibleWhen: { notice_type: 'Annat antal månader' },
        scopes: { imported: { from: 'dates.notice_period_months' } },
    },
    {
        key: 'extension_months',
        label: 'Förlängning (månader)',
        type: 'number',
        min: 0,
        integer: true,
        section: 'uppsagning',
        visibleWhen: { lease_type: 'Bestämd tid' },
    },
    // §9 Hyressättningsmodell
    {
        key: 'rent_model',
        label: 'Hyressättningsmodell',
        type: 'select',
        section: 'hyressattningsmodell',
        requiredAtFinal: true,
        options: [
            { value: 'Kollektiva hyresförhandlingar', label: 'Kollektiva hyresförhandlingar', code: 'kollektiv', summary: 'Rent set via collective negotiation (förhandlingsordning).' },
            { value: 'Tidsbestämt hyressättningsavtal', label: 'Tidsbestämt hyressättningsavtal', code: 'tidsbestamt', summary: 'Rent set by a time-limited rent-setting agreement (annex).' },
            { value: 'Direkta förhandlingar mellan parterna', label: 'Direkta förhandlingar mellan parterna', code: 'direkt', summary: 'Rent agreed directly between the parties.' },
        ],
    },
    {
        // Pattern B (ADR-0028 addendum) — picks an uploaded bilaga.
        key: 'rent_model_annex',
        label: 'Bilaga (hyressättningsavtal)',
        type: 'attachment_ref',
        section: 'hyressattningsmodell',
        help: 'Välj uppladdad bilaga med det tidsbestämda hyressättningsavtalet.',
        visibleWhen: { rent_model: 'Tidsbestämt hyressättningsavtal' },
    },
    // §10 Förhandlingsklausul — gäller alltid när rent_model = Kollektiv
    {
        key: 'negotiation_clause',
        label: 'Förhandlingsklausul gäller',
        type: 'boolean',
        section: 'forhandlingsklausul',
        default: true,
        visibleWhen: { rent_model: 'Kollektiva hyresförhandlingar' },
    },
    {
        key: 'tenant_org',
        label: 'Hyresgästorganisation',
        type: 'select_with_freetext',
        section: 'forhandlingsklausul',
        visibleWhen: { rent_model: 'Kollektiva hyresförhandlingar' },
        options: [
            { value: 'Djurgårdens hyresgästförening (802531-4702)', label: 'Djurgårdens hyresgästförening (802531-4702)', code: 'djurgarden', summary: 'Djurgårdens hyresgästförening, org.nr 802531-4702.' },
            { value: 'Föreningen för Sveriges Hyresgäster (802550-7990)', label: 'Föreningen för Sveriges Hyresgäster (802550-7990)', code: 'fsh', summary: 'Föreningen för Sveriges Hyresgäster, org.nr 802550-7990.' },
            { value: 'Göteborgs boendeförening (857205-6219)', label: 'Göteborgs boendeförening (857205-6219)', code: 'goteborg_boende', summary: 'Göteborgs boendeförening, org.nr 857205-6219.' },
            { value: 'Hyresgästföreningen (802001-5106)', label: 'Hyresgästföreningen (802001-5106)', code: 'hgf', summary: 'Hyresgästföreningen (the national tenants’ union), org.nr 802001-5106.' },
            { value: 'Hyresgästföreningen Uppsala Studentbostäder (817602-1486)', label: 'Hyresgästföreningen Uppsala Studentbostäder (817602-1486)', code: 'hgf_uppsala_student', summary: 'Hyresgästföreningen Uppsala Studentbostäder, org.nr 817602-1486.' },
            { value: 'Hyresrätt Sverige (802510-4434)', label: 'Hyresrätt Sverige (802510-4434)', code: 'hyresratt_sverige', summary: 'Hyresrätt Sverige, org.nr 802510-4434.' },
            { value: 'Råckstahyresgästernas intresseförening (802014-2876)', label: 'Råckstahyresgästernas intresseförening (802014-2876)', code: 'racksta', summary: 'Råckstahyresgästernas intresseförening, org.nr 802014-2876.' },
            { value: 'Stockholms Studenthyresgästers Förening (802009-1354)', label: 'Stockholms Studenthyresgästers Förening (802009-1354)', code: 'sshf', summary: 'Stockholms Studenthyresgästers Förening, org.nr 802009-1354.' },
        ],
    },
    // §11 Hyra
    { key: 'rent_amount', label: 'Hyra (kr/mån)', type: 'currency', section: 'hyra', requiredAtFinal: true, scopes: { imported: { from: 'rent.monthly_sek', requiredForReview: true }, copy: 'manual' } },
    {
        key: 'rent_includes_member_fee',
        label: 'Avgift till hyresgästorg. ingår',
        type: 'select',
        section: 'hyra',
        options: [
            { value: 'JA', label: 'Ja', code: 'ja', summary: 'The tenant-union fee is included in the rent.' },
            { value: 'NEJ', label: 'Nej', code: 'nej', summary: 'The tenant-union fee is not included in the rent.' },
        ],
    },
    {
        key: 'rent_includes_membership',
        label: 'Medlemskap i hyresgästorg. ingår',
        type: 'select',
        section: 'hyra',
        options: [
            { value: 'JA', label: 'Ja', code: 'ja', summary: 'Tenant-union membership is included in the rent.' },
            { value: 'NEJ', label: 'Nej', code: 'nej', summary: 'Tenant-union membership is not included in the rent.' },
        ],
    },
    {
        key: 'rent_type',
        label: 'Hyrestyp',
        type: 'select',
        section: 'hyra',
        requiredAtFinal: true,
        options: [
            { value: 'Total hyra', label: 'Total hyra', code: 'total', summary: 'Rent is all-inclusive (no separate add-ons).' },
            { value: 'Hyra exkl. tillägg', label: 'Hyra exkl. tillägg', code: 'exkl_tillagg', summary: 'Base rent excluding separately-billed add-ons.' },
        ],
    },
    // §12 Tillägg
    {
        key: 'heating_included',
        label: 'Uppvärmning',
        type: 'select',
        section: 'tillagg',
        requiredAtFinal: true,
        options: [
            { value: 'Individuell mätning och debitering', label: 'Individuell mätning och debitering', code: 'imd', summary: 'Heating billed by individual metering (IMD), on top of rent.' },
            { value: 'Ingår i hyran', label: 'Ingår i hyran', code: 'ingar', summary: 'Heating is included in the rent.' },
        ],
    },
    {
        key: 'cold_water_included',
        label: 'Kallvatten',
        type: 'select',
        section: 'tillagg',
        requiredAtFinal: true,
        options: [
            { value: 'Individuell mätning och debitering', label: 'Individuell mätning och debitering', code: 'imd', summary: 'Cold water billed by individual metering (IMD), on top of rent.' },
            { value: 'Ingår i hyran', label: 'Ingår i hyran', code: 'ingar', summary: 'Cold water is included in the rent.' },
        ],
    },
    {
        key: 'hot_water_included',
        label: 'Varmvatten',
        type: 'select',
        section: 'tillagg',
        requiredAtFinal: true,
        options: [
            { value: 'Individuell mätning och debitering', label: 'Individuell mätning och debitering', code: 'imd', summary: 'Hot water billed by individual metering (IMD), on top of rent.' },
            { value: 'Ingår i hyran', label: 'Ingår i hyran', code: 'ingar', summary: 'Hot water is included in the rent.' },
        ],
    },
    {
        key: 'electricity_included',
        label: 'Hushållsel',
        type: 'select',
        section: 'tillagg',
        requiredAtFinal: true,
        options: [
            { value: 'Eget abonnemang', label: 'Eget abonnemang', code: 'eget', summary: 'Tenant holds their own electricity subscription.' },
            { value: 'Individuell mätning och debitering', label: 'Individuell mätning och debitering', code: 'imd', summary: 'Household electricity billed by individual metering (IMD), on top of rent.' },
            { value: 'Ingår i hyran', label: 'Ingår i hyran', code: 'ingar', summary: 'Household electricity is included in the rent.' },
        ],
    },
    { key: 'extras_included', label: 'Tillval av utrustning', type: 'boolean', section: 'tillagg' },
    {
        // Pattern B (ADR-0028 addendum) — picks an uploaded bilaga.
        key: 'extras_annex',
        label: 'Bilaga (tillvalsavtal)',
        type: 'attachment_ref',
        section: 'tillagg',
        help: 'Välj uppladdad bilaga med tillvalsavtalet.',
        visibleWhen: { extras_included: true },
    },
    // §13 Hyrans betalning
    {
        key: 'payment_type',
        label: 'Betalningssätt',
        type: 'select',
        section: 'betalning',
        requiredAtFinal: true,
        options: [
            { value: 'Enligt avisering i faktura', label: 'Enligt avisering i faktura', code: 'avisering', summary: 'Paid per invoice/notice issued by the landlord.' },
            { value: 'Plusgiro', label: 'Plusgiro', code: 'plusgiro', summary: 'Paid to a plusgiro account number.' },
            { value: 'Bankgiro', label: 'Bankgiro', code: 'bankgiro', summary: 'Paid to a bankgiro account number.' },
        ],
    },
    {
        key: 'payment_account',
        label: 'Kontonummer',
        type: 'text',
        section: 'betalning',
        visibleWhen: { payment_type: ['Plusgiro', 'Bankgiro'] },
    },
    // §14 Deposition
    {
        key: 'deposit_required',
        label: 'Deposition krävs',
        type: 'select',
        section: 'deposition',
        options: [
            { value: 'NEJ', label: 'Nej', code: 'nej', summary: 'No deposit is required.' },
            { value: 'JA', label: 'Ja', code: 'ja', summary: 'A deposit is required (amount given in monthly rents).' },
        ],
        default: 'NEJ',
    },
    {
        key: 'deposit_months',
        label: 'Depositionsbelopp (månadshyror)',
        type: 'number',
        integer: true,
        min: 0,
        section: 'deposition',
        visibleWhen: { deposit_required: 'JA' },
    },
    {
        key: 'deposit_return_days',
        label: 'Återbetalning inom (dagar)',
        type: 'number',
        min: 0,
        integer: true,
        section: 'deposition',
        visibleWhen: { deposit_required: 'JA' },
        default: 30,
    },
    // Borgensman — checkbox-gates the optional `guarantor` party slot.
    {
        key: 'has_guarantor',
        label: 'Avtalet har borgensman',
        type: 'boolean',
        section: 'Borgensman',
        default: false,
        help: 'Bocka i om någon ska gå i borgen för hyran. Lägg sedan till borgensmannen i partilistan ovan.',
    },
    // §15 Garage / Bilplats
    { key: 'garage_separate_agreement', label: 'enligt särskilt avtal', type: 'boolean', section: 'garage' },
    { key: 'garage_included_in_rent', label: 'Avgift ingår i hyran', type: 'boolean', section: 'garage' },
    { key: 'garage_spot_number', label: 'Plats nr', type: 'text', section: 'garage' },
    // §16 Reservnycklar
    {
        key: 'spare_key_consent',
        label: 'Förvaring av reservnyckel hos hyresvärden',
        type: 'select',
        section: 'reservnycklar',
        requiredAtFinal: true,
        options: [
            { value: 'Medges', label: 'Medges', code: 'medges', summary: 'Landlord is permitted to keep a spare key.' },
            { value: 'Medges ej', label: 'Medges ej', code: 'medges_ej', summary: 'Landlord is not permitted to keep a spare key.' },
        ],
    },
    // §17 Särskilda bestämmelser
    { key: 'no_smoking', label: 'Rökförbud gäller', type: 'boolean', section: 'sarskilda-bestammelser', default: true },
    {
        key: 'special_conditions',
        label: 'Särskilda bestämmelser',
        type: 'textarea',
        section: 'sarskilda-bestammelser',
        help: 'Ange eventuella särskilda villkor här',
    },
];
/**
 * Realistic Swedish sample values — used by the dev gallery in
 * `mode='preview'` so iteration feedback is on a filled contract, not
 * a form skeleton. The fictional landlord/tenant names don't reflect
 * any real person.
 */
export const sampleValues = {
    landlord_name: 'Bostadsägarna Stockholm AB',
    landlord_org_number: '556789-1234',
    landlord_address: 'Drottninggatan 100\n111 60 Stockholm',
    landlord_email: 'kontakt@bostadsagarna.se',
    landlord_phone: '08-123 45 67',
    tenant_name: 'Anna Andersson',
    tenant_personnummer: '19850412-1234',
    tenant_email: 'anna.andersson@example.se',
    tenant_phone: '070-123 45 67',
    cotenant_name: 'Erik Andersson',
    cotenant_personnummer: '19830822-5678',
    cotenant_email: 'erik.andersson@example.se',
    cotenant_phone: '070-987 65 43',
    contract_number: 'A-2042',
    object_number: 'B-1102',
    property_municipality: 'Stockholm',
    property_designation: 'Klockaren 5',
    property_staircase: '2 tr',
    property_landlord_apt_no: 'A1003',
    property_address: 'Sveavägen 42\n113 59 Stockholm',
    property_official_apt_no: '1102',
    apartment_usage: 'för användning som stadigvarande bostad',
    apartment_type: '2 rum och kök',
    apartment_area: 62,
    has_attic: true,
    attic_number: 'V-14',
    has_basement: false,
    lease_type: 'Tillsvidare',
    lease_start_date: '2026-06-01',
    notice_type: '3 månader (standard)',
    rent_model: 'Kollektiva hyresförhandlingar',
    negotiation_clause: true,
    tenant_org: 'Hyresgästföreningen (802001-5106)',
    rent_amount: 12500,
    rent_includes_member_fee: 'JA',
    rent_includes_membership: 'NEJ',
    rent_type: 'Total hyra',
    heating_included: 'Ingår i hyran',
    cold_water_included: 'Ingår i hyran',
    hot_water_included: 'Ingår i hyran',
    electricity_included: 'Eget abonnemang',
    extras_included: false,
    payment_type: 'Bankgiro',
    payment_account: '123-4567',
    deposit_required: 'JA',
    deposit_months: 2,
    deposit_return_days: 30,
    garage_separate_agreement: false,
    garage_included_in_rent: false,
    spare_key_consent: 'Medges',
    no_smoking: true,
};
const cond = (mode, predicate) => mode === 'blank' ? true : predicate;
/**
 * Repeating letterhead — rendered once per printed page in the PDF, and
 * once inline (in the TemplateDocument header slot) for the SPA preview.
 * Pulls owner identity through `<OwnerLogo>` -> `useOwnerBranding()`,
 * which is set by `<OwnerBrandingProvider>` upstream in both render paths.
 */
export function Letterhead() {
    return _jsx(TemplateLetterhead, { title: metadata.title, formNumber: metadata.formNumber });
}
/**
 * Page footer — repeats at the bottom of every printed page. Today this
 * is pure presentational text; if page numbers are added later, use
 * Chromium's `<span class="pageNumber"/>` / `totalPages` hooks (per
 * Gotenberg's HTML conversion contract).
 */
export function PageFooter() {
    return _jsx(TemplateFooter, { formNumber: metadata.formNumber });
}
/**
 * Flowing body content — sections, clauses, signature block. Paginated
 * by Chromium in the PDF render (break-inside: avoid on <Section> and
 * <KeepTogether> keeps related rows together). Stacked inline in the
 * SPA preview's tall-box.
 */
export function Body({ values, mode, attachments, signatories, }) {
    const hasCotenant = cond(mode, !!(values.cotenant_name || values.cotenant_personnummer || values.cotenant_email || values.cotenant_phone));
    return (_jsxs(SectionNumberingProvider, { sections: metadata.sections ?? [], values: values, mode: mode, children: [_jsxs("div", { className: "mb-2 flex flex-wrap gap-x-8 gap-y-1 text-[10px] text-stone-700", children: [_jsxs("div", { children: [_jsx("span", { className: "font-sans uppercase tracking-[0.1em] text-stone-500", children: "Avtalsnummer:" }), ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.contract_number) })] }), cond(mode, !!values.object_number) && (_jsxs("div", { children: [_jsx("span", { className: "font-sans uppercase tracking-[0.1em] text-stone-500", children: "Objektsnummer:" }), ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.object_number) })] }))] }), _jsx("p", { className: "mb-4 text-[10px] italic text-stone-600", children: metadata.preamble }), _jsxs("div", { className: "grid grid-cols-2 gap-6", children: [_jsx(Section, { sectionRef: "hyresvard", title: "Hyresv\u00E4rd", children: _jsx(PartyBlock, { metadata: metadata, slot: "lessor", values: values }) }), _jsxs(Section, { sectionRef: "hyresgast", title: "Hyresg\u00E4st", children: [_jsx(PartyBlock, { metadata: metadata, slot: "lessee_primary", values: values }), hasCotenant && (_jsx(PartyBlock, { metadata: metadata, slot: "lessee_co", values: values, subheading: "Medhyresg\u00E4st" }))] })] }), _jsxs(Section, { sectionRef: "lagenhets-adress", title: "L\u00E4genhetens adress", children: [_jsx(FieldRow, { label: "Kommun", children: v(values.property_municipality) }), _jsx(FieldRow, { label: "Fastighetsbeteckning", children: v(values.property_designation) }), _jsx(FieldRow, { label: "Trappor", children: v(values.property_staircase) }), _jsx(FieldRow, { label: "Postadress", children: v(values.property_address) }), _jsx(FieldRow, { label: "Officiellt l\u00E4genhetsnummer", children: v(values.property_official_apt_no) }), cond(mode, Boolean(values.property_landlord_apt_no)) && (_jsx(FieldRow, { label: "Hyresv\u00E4rdens l\u00E4genhetsnummer", children: v(values.property_landlord_apt_no) }))] }), _jsxs("div", { className: "grid grid-cols-2 gap-6", children: [_jsxs(Section, { sectionRef: "anvandning", title: "L\u00E4genhetens anv\u00E4ndning", children: [_jsx(FieldRow, { label: "Anv\u00E4ndning", children: v(values.apartment_usage) }), cond(mode, values.apartment_usage === 'annat') && (_jsx(FieldRow, { label: "Annat \u00E4ndam\u00E5l", children: v(values.apartment_usage_other) }))] }), _jsxs(Section, { sectionRef: "storlek", title: "L\u00E4genhetens storlek", children: [_jsx(FieldRow, { label: "L\u00E4genhetstyp", children: v(values.apartment_type) }), _jsx(FieldRow, { label: "Area (m\u00B2)", children: num(values.apartment_area) })] })] }), _jsx(Section, { sectionRef: "tillbehor", title: "Till l\u00E4genheten h\u00F6r", children: _jsxs("div", { className: "grid grid-cols-2 gap-6", children: [_jsxs("div", { children: [_jsx(FieldRow, { label: "Vindsutrymme ing\u00E5r", children: values.has_attic === true ? '☑' : '☐' }), cond(mode, values.has_attic === true) && (_jsx(FieldRow, { label: "Vindsutrymme nr", children: v(values.attic_number) }))] }), _jsxs("div", { children: [_jsx(FieldRow, { label: "K\u00E4llarutrymme ing\u00E5r", children: values.has_basement === true ? '☑' : '☐' }), cond(mode, values.has_basement === true) && (_jsx(FieldRow, { label: "K\u00E4llarutrymme nr", children: v(values.basement_number) }))] })] }) }), _jsxs(Section, { sectionRef: "hyrestid", title: "Hyrestid", children: [_jsx(FieldRow, { label: "Hyresform", children: v(values.lease_type) }), _jsx(FieldRow, { label: "Startdatum", children: v(values.lease_start_date) }), cond(mode, values.lease_type === 'Bestämd tid') && (_jsx(FieldRow, { label: "Slutdatum", children: v(values.lease_end_date) }))] }), _jsxs(Section, { sectionRef: "uppsagning", title: "Upps\u00E4gning", children: [_jsx(FieldRow, { label: "Upps\u00E4gningstyp", children: v(values.notice_type) }), cond(mode, values.notice_type === 'Annat antal månader') && (_jsx(FieldRow, { label: "Antal m\u00E5nader", children: num(values.notice_months) })), cond(mode, values.lease_type === 'Bestämd tid') && (_jsx(FieldRow, { label: "F\u00F6rl\u00E4ngning (m\u00E5nader)", children: num(values.extension_months) }))] }), _jsxs(Section, { sectionRef: "hyressattningsmodell", title: "Hyress\u00E4ttningsmodell", children: [_jsx(FieldRow, { label: "Modell", children: v(values.rent_model) }), cond(mode, values.rent_model === 'Tidsbestämt hyressättningsavtal') && (_jsx(FieldRow, { label: "Bilaga (hyress\u00E4ttningsavtal)", children: attachmentLabel(attachments, values.rent_model_annex) }))] }), _jsxs(Section, { sectionRef: "forhandlingsklausul", title: "F\u00F6rhandlingsklausul", children: [_jsx(FieldRow, { label: "F\u00F6rhandlingsklausul g\u00E4ller", children: values.negotiation_clause === true ? '☑' : '☐' }), _jsx(FieldRow, { label: "Hyresg\u00E4storganisation", children: v(values.tenant_org) })] }), _jsxs(Section, { sectionRef: "hyra", title: "Hyra", children: [_jsx(FieldRow, { label: "Hyra (kr/m\u00E5n)", children: num(values.rent_amount) }), _jsx(FieldRow, { label: "Hyrestyp", children: v(values.rent_type) }), _jsx(FieldRow, { label: "Avgift till hyresg\u00E4storg. ing\u00E5r", children: v(values.rent_includes_member_fee) }), _jsx(FieldRow, { label: "Medlemskap i hyresg\u00E4storg. ing\u00E5r", children: v(values.rent_includes_membership) })] }), _jsxs(Section, { sectionRef: "tillagg", title: "Till\u00E4gg", children: [_jsx(FieldRow, { label: "Uppv\u00E4rmning", children: v(values.heating_included) }), _jsx(FieldRow, { label: "Kallvatten", children: v(values.cold_water_included) }), _jsx(FieldRow, { label: "Varmvatten", children: v(values.hot_water_included) }), _jsx(FieldRow, { label: "Hush\u00E5llsel", children: v(values.electricity_included) }), _jsx(FieldRow, { label: "Tillval av utrustning", children: values.extras_included === true ? '☑' : '☐' }), cond(mode, values.extras_included === true) && (_jsx(FieldRow, { label: "Bilaga (tillvalsavtal)", children: attachmentLabel(attachments, values.extras_annex) }))] }), _jsxs(Section, { sectionRef: "betalning", title: "Hyrans betalning", children: [_jsx(FieldRow, { label: "Betalningss\u00E4tt", children: v(values.payment_type) }), cond(mode, values.payment_type === 'Plusgiro' || values.payment_type === 'Bankgiro') && (_jsx(FieldRow, { label: "Kontonummer", children: v(values.payment_account) }))] }), _jsxs(Section, { sectionRef: "deposition", title: "Deposition", children: [_jsx(FieldRow, { label: "Deposition kr\u00E4vs", children: v(values.deposit_required) }), cond(mode, values.deposit_required === 'JA') && (_jsxs(_Fragment, { children: [_jsx(FieldRow, { label: "Depositionsbelopp (m\u00E5nadshyror)", children: num(values.deposit_months) }), _jsx(FieldRow, { label: "\u00C5terbetalning inom (dagar)", children: num(values.deposit_return_days) })] }))] }), _jsxs(Section, { sectionRef: "garage", title: "Garage / Bilplats", children: [_jsx(FieldRow, { label: "enligt s\u00E4rskilt avtal", children: values.garage_separate_agreement === true ? '☑' : '☐' }), _jsx(FieldRow, { label: "Avgift ing\u00E5r i hyran", children: values.garage_included_in_rent === true ? '☑' : '☐' }), _jsx(FieldRow, { label: "Plats nr", children: v(values.garage_spot_number) })] }), _jsx(Section, { sectionRef: "reservnycklar", title: "Reservnycklar", children: _jsx(FieldRow, { label: "F\u00F6rvaring av reservnyckel hos hyresv\u00E4rden", children: v(values.spare_key_consent) }) }), _jsxs(Section, { sectionRef: "sarskilda-bestammelser", title: "S\u00E4rskilda best\u00E4mmelser", children: [_jsx(FieldRow, { label: "R\u00F6kf\u00F6rbud g\u00E4ller", children: values.no_smoking === true ? '☑' : '☐' }), cond(mode, !!values.special_conditions) && (_jsx(FieldRow, { label: "\u00D6vrigt", children: v(values.special_conditions) }))] }), _jsx(ClauseBlock, { title: "Hinder och men i nyttjander\u00E4tten", body: "Om hinder eller men uppst\u00E5r i nyttjander\u00E4tten utan hyresg\u00E4stens v\u00E5llande har hyresg\u00E4sten r\u00E4tt till sk\u00E4lig neds\u00E4ttning av hyran f\u00F6r den tid som l\u00E4genheten \u00E4r i bristf\u00E4lligt skick. Hyresg\u00E4sten har inte r\u00E4tt till neds\u00E4ttning av hyran under tiden d\u00E5 hyresv\u00E4rden utf\u00F6r sedvanligt underh\u00E5ll i l\u00E4genheten eller fastigheten under f\u00F6ruts\u00E4ttning av att arbetet utf\u00F6rs utan on\u00F6dig tidsutdr\u00E4kt." }), _jsx(ClauseBlock, { title: "Hyresg\u00E4stens \u00E5taganden", body: 'Hyresgästen åtar sig:\n' +
                    '• att iaktta sin vårdplikt i sin användning av lägenheten och vad som hör till den samt vid användning av de gemensamma utrymmena.\n' +
                    '• att inte utan hyresvärdens samtycke eller hyresnämndens tillstånd upplåta lägenheten i andra hand.\n' +
                    '• att inte låta fler personer stadigvarande bo i lägenheten än vad den är avsedd för.\n' +
                    '• att på egen bekostnad forsla bort avfall som är olämpligt för soptunna eller sopnedkast.\n' +
                    '• att hålla till lägenheten hörande balkong eller uteplats fri från snö och is.\n' +
                    '• att lämna hyresvärden tillträde till lägenheten för tillsyn eller förbättringsarbete.\n' +
                    '• att när lägenheten sagts upp, låta den visas för ny hyresgäst på lämplig tid.\n' +
                    '• att vid flytt överlämna samtliga port- och dörrnycklar samt städa lägenheten noggrant.' }), cond(mode, values.no_smoking === true) && (_jsx(ClauseBlock, { title: "R\u00F6kf\u00F6rbud", body: "R\u00F6kning \u00E4r inte till\u00E5ten i l\u00E4genheten eller husets allm\u00E4nna och gemensamma utrymmen." })), _jsx(ClauseBlock, { title: "Force Majeure", body: "Hyresv\u00E4rden fritar sig fr\u00E5n skyldighet att fullg\u00F6ra sin del av avtalet och fr\u00E5n skyldighet att betala skadest\u00E5nd om \u00E5tagandena inte alls eller endast till onormalt h\u00F6g kostnad kan fullg\u00F6ras p\u00E5 grund av krig, upplopp, arbetsinst\u00E4llelse, blockad, eldsv\u00E5da, explosion eller ingrepp av offentlig myndighet som hyresv\u00E4rden inte r\u00E5der \u00F6ver och inte heller kunnat f\u00F6rutse." }), _jsx(ClauseBlock, { title: "Tidigare hyresavtal", body: "Eventuellt tidigare hyresavtal mellan parterna avseende denna l\u00E4genhet upph\u00F6r att g\u00E4lla fr.o.m. dagen f\u00F6r detta avtals ikrafttr\u00E4dande." }), _jsx(Section, { sectionRef: "underskrift", title: "Underskrifter", children: _jsx(KeepTogether, { children: _jsx(SigningBlock, { statement: true, parties: [
                            { label: 'Hyresvärd', role: 'lessor', ordinal: 0 },
                            { label: 'Hyresgäst', role: 'lessee', ordinal: 0 },
                            ...(cond(mode, !!values.cotenant_name)
                                ? [{ label: 'Medhyresgäst', role: 'lessee', ordinal: 1 }]
                                : []),
                            ...(cond(mode, values.has_guarantor === true)
                                ? [{ label: 'Borgensman', role: 'guarantor', ordinal: 0 }]
                                : []),
                        ], signatories: signatories }) }) })] }));
}
/**
 * SPA preview entry. Composes Letterhead + Body + PageFooter inside
 * TemplateDocument so the live editor gets the familiar A4-shaped
 * tall-box. The PDF render bypasses this and SSRs each subtree on
 * its own — see apps/regta-backend/src/routes/contracts-render.ts.
 */
export function Component(props) {
    return (_jsx(TemplateDocument, { header: _jsx(Letterhead, {}), footer: _jsx(PageFooter, {}), children: _jsx(Body, { ...props }) }));
}
//# sourceMappingURL=index.js.map